/* The game shell: KPI header, decision tabs, and the submit-quarter flow.
 * Every screen reads the server's game state and stages edits in the decisions
 * store; submitting sends them to the server, which resolves the quarter. */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SubmitRoundInput } from '@venturewrist/shared';
import { useGame, useAdvance } from '../hooks/queries';
import { useDecisions, pendingCount } from '../store/decisions';
import { Spinner, Badge, Button } from '../components/ui';
import { money } from '../lib/format';
import { ApiError } from '../lib/api';
import type { AdvanceResponse } from '../lib/types';
import { StudentTutorial, studentScopeKey } from '../tutorial/StudentTutorial';
import { useTutorial } from '../tutorial/useTutorial';
import { studentSeries } from '../tutorial/steps.student';
import { shapeOf } from '../tutorial/types';
import { Overview } from './screens/Overview';
import { Products } from './screens/Products';
import { Pricing } from './screens/Pricing';
import { Marketing } from './screens/Marketing';
import { SupplyChain } from './screens/SupplyChain';
import { Distribution } from './screens/Distribution';
import { Logistics } from './screens/Logistics';
import { Financing } from './screens/Financing';
import { RnD } from './screens/RnD';
import { Production } from './screens/Production';
import { Intel } from './screens/Intel';
import { Reports } from './screens/Reports';
import { MyActivity } from './screens/MyActivity';
import { Academy } from './screens/Academy';
import { FinalReview } from './screens/FinalReview';
import type { ScreenProps } from './screens/types';

const TABS: Array<{ id: string; label: string; icon: string; Comp: (p: ScreenProps) => JSX.Element }> = [
  { id: 'overview', label: 'Overview', icon: '📊', Comp: Overview },
  { id: 'products', label: 'Design', icon: '⌚', Comp: Products },
  { id: 'pricing', label: 'Pricing', icon: '💲', Comp: Pricing },
  { id: 'production', label: 'Production', icon: '🏭', Comp: Production },
  { id: 'marketing', label: 'Marketing', icon: '📣', Comp: Marketing },
  { id: 'supply', label: 'Supply', icon: '🔧', Comp: SupplyChain },
  { id: 'distribution', label: 'Distribution', icon: '🌐', Comp: Distribution },
  { id: 'logistics', label: 'Logistics', icon: '📦', Comp: Logistics },
  { id: 'rnd', label: 'R&D', icon: '🔬', Comp: RnD },
  { id: 'financing', label: 'Financing', icon: '💰', Comp: Financing },
  { id: 'intel', label: 'Intel', icon: '🛰️', Comp: Intel },
  { id: 'reports', label: 'Reports', icon: '📊', Comp: Reports },
  { id: 'activity', label: 'My Activity', icon: '📝', Comp: MyActivity },
  { id: 'academy', label: 'Academy', icon: '🎓', Comp: Academy },
];

export function GamePlay() {
  const { gameId } = useParams<{ gameId: string }>();
  const gameQ = useGame(gameId);
  const advance = useAdvance(gameId!);
  const init = useDecisions((z) => z.init);
  const clear = useDecisions((z) => z.clear);
  const draft = useDecisions((z) => z.draft);
  const [tab, setTab] = useState('overview');
  const [result, setResult] = useState<AdvanceResponse | null>(null);

  const detail = gameQ.data;
  const round = detail?.state.round as number | undefined;

  useEffect(() => {
    if (gameId && round != null) init(gameId, round);
  }, [gameId, round, init]);

  const pending = useMemo(() => pendingCount(draft), [draft]);

  if (gameQ.isLoading || !detail) return <Spinner label="Loading game…" />;

  const s = detail.state as any;
  const finished = detail.game.status === 'finished';
  const lastComposite = detail.snapshots.at(-1)?.composite ?? null;
  // 🏁 appears once the game is finished (the engine's final news points here).
  const tabs = finished
    ? [...TABS, { id: 'final', label: 'Final Review', icon: '🏁', Comp: FinalReview }]
    : TABS;

  const submit = () => {
    const payload: SubmitRoundInput = { round: s.round, decisions: draft };
    advance.mutate(payload, {
      onSuccess: (res) => { setResult(res); clear(); },
    });
  };

  const Active = (tabs.find((t) => t.id === tab) ?? tabs[0]!).Comp;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/dashboard" className="text-xs text-mut hover:text-txt">← Dashboard</Link>
          <h1 className="text-2xl font-extrabold tracking-tight">{detail.game.companyName}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="mut">Cash {money(s.cash)}</Badge>
          {s.insolvent ? <Badge tone="bad">⚠ Insolvency watch</Badge> : (s.loan ?? 0) > 0 ? <Badge tone="warn">Overdraft {money(s.loan)}</Badge> : null}
          <Badge tone="acc">Brand {Math.round(s.brand ?? 0)}</Badge>
          {lastComposite != null && <Badge tone="acc">Composite {Math.round(lastComposite)}</Badge>}
          <Badge tone={finished ? 'mut' : 'good'}>Quarter {s.round} / {s.maxRounds}</Badge>
          <button
            className="btn-ghost px-2.5 py-1 text-sm"
            title="Restart the guided tour"
            onClick={() => useTutorial.getState().restart('student', shapeOf(studentSeries), studentScopeKey(detail.game.teamId))}
          >
            ? Tour
          </button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 rounded-sm border border-card-b bg-panel/40 p-1">
        {tabs.map((t) => (
          <button key={t.id} data-tut={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`tab ${tab === t.id ? 'tab-active' : 'tab-idle'}`}>
            <span className="mr-1">{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>

      <Active detail={detail} />

      <div className="sticky bottom-4 z-10">
        <div className="card flex items-center justify-between p-3">
          <div className="text-sm text-mut" data-tut="staged-count">
            {finished ? (
              <span className="text-txt">This game is complete.</span>
            ) : (
              <>You have <b className="text-txt">{pending}</b> staged change{pending === 1 ? '' : 's'} for Q{s.round}.</>
            )}
            {advance.error && <span className="ml-2 text-bad">{(advance.error as ApiError).message}</span>}
          </div>
          <span data-tut="submit-quarter">
            <Button onClick={submit} disabled={finished || advance.isPending}>
              {advance.isPending ? 'Resolving…' : `Submit Q${s.round}`}
            </Button>
          </span>
        </div>
      </div>

      {result && <ResultModalLazy res={result} onClose={() => { const done = result.finished; setResult(null); setTab(done ? 'final' : 'overview'); }} />}

      <StudentTutorial detail={detail} tab={tab} resultOpen={!!result} />
    </div>
  );
}

import { ResultModal } from './ResultModal';
function ResultModalLazy({ res, onClose }: { res: AdvanceResponse; onClose: () => void }) {
  return <ResultModal res={res} onClose={onClose} />;
}
